"use client";

import { useState } from "react";
import dynamic from "next/dynamic";
import { Box, Building2, Sofa, Square } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { LivePreview } from "@/components/configurator/live-preview";
import { DimensionCards } from "@/components/configurator/dimension-cards";
import type { FontOption } from "@/lib/fonts";
import type { ColorOption } from "@/lib/colors";
import type { SceneType } from "@/components/configurator/three/preview-3d";

// The 3D preview pulls in three.js + react-three-fiber and only makes
// sense in the browser (WebGL) - loaded on demand, client-side only, so
// switching to it never costs anything for people who stay on 2D.
const Preview3D = dynamic(
  () => import("@/components/configurator/three/preview-3d"),
  {
    ssr: false,
    loading: () => (
      <div className="flex h-64 items-center justify-center rounded-xl border bg-muted text-sm text-muted-foreground sm:h-80">
        3D-Vorschau wird geladen…
      </div>
    ),
  }
);

interface PreviewPanelProps {
  text: string;
  font: FontOption;
  color: ColorOption;
  widthCm: number;
  heightCm: number;
  /** Extrusion depth of the letters, in cm - only the 3D preview uses this. */
  depthCm: number;
}

export function PreviewPanel(props: PreviewPanelProps) {
  const [mode, setMode] = useState<"2d" | "3d">("2d");
  const [scene, setScene] = useState<SceneType>("livingRoom");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          Vorschau
        </p>
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={mode}
          onValueChange={(value) => value && setMode(value as "2d" | "3d")}
        >
          <ToggleGroupItem value="2d" aria-label="2D-Vorschau" className="gap-1.5 px-3">
            <Square className="size-3.5" />
            2D
          </ToggleGroupItem>
          <ToggleGroupItem value="3d" aria-label="3D-Vorschau an der Wand" className="gap-1.5 px-3">
            <Box className="size-3.5" />
            3D
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {mode === "3d" ? (
        <div className="flex justify-end">
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={scene}
            onValueChange={(value) => value && setScene(value as SceneType)}
          >
            <ToggleGroupItem
              value="livingRoom"
              aria-label="Wohnzimmer-Ansicht"
              className="gap-1.5 px-3"
            >
              <Sofa className="size-3.5" />
              Wohnzimmer
            </ToggleGroupItem>
            <ToggleGroupItem value="office" aria-label="Office-Ansicht" className="gap-1.5 px-3">
              <Building2 className="size-3.5" />
              Office
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      ) : null}

      {mode === "2d" ? (
        <LivePreview {...props} />
      ) : (
        <Preview3D
          text={props.text}
          font={props.font}
          color={props.color}
          widthCm={props.widthCm}
          heightCm={props.heightCm}
          depthCm={props.depthCm}
          scene={scene}
        />
      )}

      <DimensionCards widthCm={props.widthCm} heightCm={props.heightCm} />
    </div>
  );
}
